import { z } from 'zod';

import { makeEndpoint, paginateResponseSchema } from '../common';

export enum AutomationFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum DayOfWeek {
  SUNDAY = 'SUNDAY',
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
}

export const reportAutomationConfigSchema = z.object({
  id: z.string(),
  reportSavedConfigId: z.string(),
  recipientEmails: z.array(z.string()),
  frequency: z.nativeEnum(AutomationFrequency),
  time: z.string(),
  dayOfWeek: z.nativeEnum(DayOfWeek).nullable(),
  dayOfMonth: z.number().nullable(),
  nextScheduledAt: z.coerce.date(),
  timezone: z.string(),
  isActive: z.boolean(),
});

const createAutomationRequestSchema = z.object({
  recipientEmails: z.array(z.string().email()).min(1),
  frequency: z.nativeEnum(AutomationFrequency),
  time: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format'),
  dayOfWeek: z.nativeEnum(DayOfWeek).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  timezone: z.string().min(1),
  isActive: z.boolean().optional().default(false),
});

const updateAutomationRequestSchema = z.object({
  recipientEmails: z.array(z.string().email()).min(1).optional(),
  frequency: z.nativeEnum(AutomationFrequency).optional(),
  time: z
    .string()
    .regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format')
    .optional(),
  dayOfWeek: z.nativeEnum(DayOfWeek).nullable().optional(),
  dayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
  timezone: z.string().min(1).optional(),
  isActive: z.boolean().optional(),
});

export const CreateAutomationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, configId }: { brandId: string; configId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}/automations`,
  responseSchema: reportAutomationConfigSchema,
  requestSchema: createAutomationRequestSchema,
});

export const ListAutomationsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, configId }: { brandId: string; configId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}/automations`,
  responseSchema: paginateResponseSchema(reportAutomationConfigSchema),
  requestSchema: z.void(),
});

export const GetAutomationEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, configId, automationId }: { brandId: string; configId: string; automationId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}/automations/${automationId}`,
  responseSchema: reportAutomationConfigSchema,
  requestSchema: z.void(),
});

export const UpdateAutomationEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, configId, automationId }: { brandId: string; configId: string; automationId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}/automations/${automationId}`,
  responseSchema: reportAutomationConfigSchema,
  requestSchema: updateAutomationRequestSchema,
});

export const DeleteAutomationEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, configId, automationId }: { brandId: string; configId: string; automationId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}/automations/${automationId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
